// Saisie de photos sur le terrain : appareil photo ou galerie, réduction immédiate sur l'appareil
// (1 600 px, JPEG, empreinte SHA-256, D-051), vignettes avec dimensions et poids, retrait. Les
// photos ne quittent l'appareil qu'à la synchronisation, après l'opération qui les référence.
import { Bouton } from '@cc/ui';
import { Camera } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { uuidV7 } from '../../terrain/identifiants';
import { reduirePhoto, tailleLisible } from '../../terrain/photos';
import type { PhotoAEnregistrer } from '../../terrain/stockage';
import styles from './terrain.module.css';

export interface PhotoSaisie {
  id_client: string;
  fichier: Blob;
  apercu: string;
  largeur: number;
  hauteur: number;
  taille: number;
  empreinte: string;
  prise_le: string;
}

export interface ProprietesSaisiePhotos {
  photos: PhotoSaisie[];
  onChange: (photos: PhotoSaisie[]) => void;
  libelle?: string;
  aide?: string;
  max?: number;
  id?: string;
}

/** Convertit les photos saisies en photos à enregistrer avec une opération. */
export function versPhotosAEnregistrer(photos: PhotoSaisie[], objetType: PhotoAEnregistrer['objet_type']): PhotoAEnregistrer[] {
  return photos.map((p) => ({ id_client: p.id_client, objet_type: objetType, fichier: p.fichier, empreinte: p.empreinte, largeur: p.largeur, hauteur: p.hauteur, prise_le: p.prise_le }));
}

export function SaisiePhotos({ photos, onChange, libelle = 'Photos', aide, max = 8, id }: ProprietesSaisiePhotos) {
  const genere = useId();
  const ident = id ?? `photos-${genere}`;
  const entree = useRef<HTMLInputElement>(null);
  const [enCours, setEnCours] = useState(0);
  const [erreurs, setErreurs] = useState<string[]>([]);
  const apercus = useRef<string[]>([]);

  useEffect(() => {
    const courants = apercus.current;
    return () => {
      for (const u of courants) URL.revokeObjectURL(u);
    };
  }, []);

  const traiter = async (fichiers: File[]) => {
    const fautes: string[] = [];
    const ajoutees: PhotoSaisie[] = [];
    for (const f of fichiers) {
      if (photos.length + ajoutees.length >= max) {
        fautes.push(`Au plus ${max} photos par rapport : ${f.name} n'a pas été ajoutée.`);
        continue;
      }
      setEnCours((n) => n + 1);
      try {
        const r = await reduirePhoto(f);
        const apercu = URL.createObjectURL(r.fichier);
        apercus.current.push(apercu);
        ajoutees.push({ id_client: uuidV7(), fichier: r.fichier, apercu, largeur: r.largeur, hauteur: r.hauteur, taille: r.taille, empreinte: r.empreinte, prise_le: new Date(f.lastModified || Date.now()).toISOString() });
      } catch (e) {
        fautes.push(`${f.name} : ${e instanceof Error ? e.message : 'photo illisible'}`);
      } finally {
        setEnCours((n) => n - 1);
      }
    }
    setErreurs(fautes);
    if (ajoutees.length) onChange([...photos, ...ajoutees]);
    if (entree.current) entree.current.value = '';
  };

  const retirer = (idClient: string) => {
    const p = photos.find((x) => x.id_client === idClient);
    if (p) URL.revokeObjectURL(p.apercu);
    onChange(photos.filter((x) => x.id_client !== idClient));
  };

  return (
    <div className={styles.pile}>
      <div className={styles.ligneBoutons}>
        <label htmlFor={ident} className={styles.aide}>
          {libelle} ({photos.length}
          {max ? ` sur ${max}` : ''})
        </label>
        <input ref={entree} id={ident} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" multiple className="cc-masque" onChange={(e) => void traiter(Array.from(e.target.files ?? []))} />
        <Bouton variante="secondaire" icone={Camera} onClick={() => entree.current?.click()} chargement={enCours > 0} libelleChargement="Réduction de la photo en cours" disabled={photos.length >= max}>
          Prendre ou choisir une photo
        </Bouton>
      </div>
      {aide ? <p className={styles.aide}>{aide}</p> : null}
      {erreurs.length ? (
        <ul className={styles.aide} role="alert">
          {erreurs.map((e) => (
            <li key={e}>{e}</li>
          ))}
        </ul>
      ) : null}
      {photos.length ? (
        <ul className={styles.photos} aria-label={`${libelle} saisies`}>
          {photos.map((p, i) => (
            <li key={p.id_client} className={styles.photo}>
              <img src={p.apercu} alt={`Photo ${i + 1}, ${p.largeur} sur ${p.hauteur} pixels`} width={p.largeur} height={p.hauteur} />
              <span>
                {p.largeur}×{p.hauteur}, {tailleLisible(p.taille)}
              </span>
              <Bouton petit variante="discret" onClick={() => retirer(p.id_client)} aria-label={`Retirer la photo ${i + 1}`}>
                Retirer
              </Bouton>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
